<template>
  <tr>
    <!-- Package Name -->
    <td class="p-4 whitespace-nowrap">
      <div class="text-sm font-medium text-gray-900 flex items-center space-x-2">
        <RouterLink
          :to="{
            name: 'PackagesDashboard',
            params: {
              projectId: $route.params.projectId,
              packageId: subcontractorPackage.id
            }
          }"
          class="btn-icon btn-icon-blue block"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
        </RouterLink>

        <span>
          {{ subcontractorPackage.name }}
        </span>
      </div>
    </td>

    <!-- Budget (Awarded of Budget) -->
    <td class="p-4 whitespace-nowrap">
      <div class="text-sm text-gray-900">
        {{ $tcc(subcontractorPackage.totalAwarded) }}
        <p class="text-xs text-gray-500">
          of {{ $tcc(subcontractorPackage.totalBudget) }}
        </p>
      </div>
    </td>

    <!-- Approved Variation -->
    <td class="p-4 whitespace-nowrap">
      <div class="text-sm text-gray-900">
        {{ $tcc(approvedAmount) }}
      </div>
    </td>

    <!-- Adjusted Value -->
    <td class="p-4 whitespace-nowrap">
      <div class="text-sm text-gray-900">
        {{ adjustedValueDisplay }}
      </div>
    </td>

    <!-- Estimated Value -->
    <td class="p-4 whitespace-nowrap">
      <div class="text-sm text-gray-900">
        {{ estimatedValueDisplay }}
      </div>
    </td>

    <!-- Status Breakdown -->
    <td class="p-4 whitespace-nowrap">
      <div class="flex flex-wrap gap-1">
        <div
          v-if="statusBreakdown.raised > 0"
          class="px-2 py-1 text-xs rounded-full"
          :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.raised)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.raised)] }"
        >
          {{ statusBreakdown.raised }} Raised
        </div>

        <div
          v-if="statusBreakdown.registered > 0"
          class="px-2 py-1 text-xs rounded-full"
          :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.registered)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.registered)] }"
        >
          {{ statusBreakdown.registered }} Registered
        </div>

        <div
          v-if="statusBreakdown.priced > 0"
          class="px-2 py-1 text-xs rounded-full"
          :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.priced)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.priced)] }"
        >
          {{ statusBreakdown.priced }} Priced
        </div>

        <div
          v-if="statusBreakdown.submitted > 0"
          class="px-2 py-1 text-xs rounded-full"
          :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.submitted)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.submitted)] }"
        >
          {{ statusBreakdown.submitted }} Submitted
        </div>

        <div
          v-if="statusBreakdown.provisioned > 0"
          class="px-2 py-1 text-xs rounded-full"
          :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.provisioned)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.provisioned)] }"
        >
          {{ statusBreakdown.provisioned }} Provisioned
        </div>

        <div
          v-if="statusBreakdown.principled > 0"
          class="px-2 py-1 text-xs rounded-full"
          :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.principled)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.principled)] }"
        >
          {{ statusBreakdown.principled }} Approved in Principle
        </div>

        <div
          v-if="statusBreakdown.approved > 0"
          class="px-2 py-1 text-xs rounded-full"
          :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.approved)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.approved)] }"
        >
          {{ statusBreakdown.approved }} Approved
        </div>

        <div
          v-if="statusBreakdown.completed > 0"
          class="px-2 py-1 text-xs rounded-full"
          :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.completed)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.completed)] }"
        >
          {{ statusBreakdown.completed }} Completed
        </div>

        <div
          v-if="statusBreakdown.declined > 0"
          class="px-2 py-1 text-xs rounded-full"
          :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.declined)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, VariationOrderStatus.declined)] }"
        >
          {{ statusBreakdown.declined }} Declined
        </div>

        <div
          v-if="variationOrdersCount === 0"
          class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
        >
          No variations
        </div>
      </div>
    </td>

    <!-- Actions -->
    <td class="p-4">
      <div class="flex justify-end">
        <RouterLink
          :to="{
            name: 'PackageVariationsSchedule',
            params: {
              projectId: $route.params.projectId,
              packageId: subcontractorPackage.id
            }
          }"
          class="btn--secondary-blue btn btn--small flex items-center space-x-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>

          <span>
            Variations
          </span>
        </RouterLink>
      </div>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { australianCurrency, enumKeyFromValue } from '@/helpers';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import {
  VariationOrderStatus,
  VariationOrderStatusColours,
  VariationOrderStatusTextColours,
} from '@/models/VariationOrder';
import { computed } from 'vue';

const props = defineProps<{
  subcontractorPackage: Package;
  projectSupplier: ProjectSupplier;
  packageSummary?: SubcontractVariationOrderDashboardPackageSummary;
}>();

const variationOrders = computed(() => {
  return props.subcontractorPackage.variation_orders || [];
});

const awardedValue = computed(() => {
  return props.subcontractorPackage.totalAwarded || 0;
});

const budgetValue = computed(() => {
  return props.subcontractorPackage.totalBudget || 0;
});

const approvedAmount = computed(() => {
  return props.packageSummary?.approved_pricing || 0;
});

const unapprovedAmount = computed(() => {
  return props.packageSummary?.unapproved_pricing || 0;
});

const adjustedValueDisplay = computed(() => {
  const adjusted = awardedValue.value + approvedAmount.value;

  return australianCurrency(adjusted);
});

const estimatedValueDisplay = computed(() => {
  const estimated = awardedValue.value + approvedAmount.value + unapprovedAmount.value;

  return australianCurrency(estimated);
});

const variationOrdersCount = computed(() => {
  return props.packageSummary?.total_variation_orders ?? variationOrders.value.length;
});

const statusBreakdown = computed(() => {
  if(props.packageSummary?.status_breakdown) {
    return props.packageSummary.status_breakdown;
  }

  // Fallback to calculating from variation orders if summary not available
  const breakdown = {
    raised: 0,
    registered: 0,
    priced: 0,
    submitted: 0,
    provisioned: 0,
    principled: 0,
    approved: 0,
    completed: 0,
    declined: 0,
  };

  variationOrders.value.forEach((vo) => {
    if(vo.status === VariationOrderStatus.raised) {
      breakdown.raised++;
    } else if(vo.status === VariationOrderStatus.registered) {
      breakdown.registered++;
    } else if(vo.status === VariationOrderStatus.submitted) {
      breakdown.submitted++;
    } else if(vo.status === VariationOrderStatus.provisioned) {
      breakdown.provisioned++;
    } else if(vo.status === VariationOrderStatus.approved) {
      breakdown.approved++;
    }
  });

  return breakdown;
});
</script>

<style scoped></style>
